import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from "lucide-react";
import type React from "react";
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import type { RootState } from "../../store";
import { removeNotification } from "../../store/slices/uiSlice";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";

const NotificationContainer: React.FC = () => {
  const { notifications } = useSelector((state: RootState) => state.ui);
  const dispatch = useDispatch();

  const getIcon = (type: string) => {
    switch (type) {
      case "success":
        return <CheckCircle className='h-4 w-4' />;
      case "error":
        return <AlertCircle className='h-4 w-4' />;
      case "warning":
        return <AlertTriangle className='h-4 w-4' />;
      case "info":
        return <Info className='h-4 w-4' />;
      default:
        return <Info className='h-4 w-4' />;
    }
  };

  const getVariant = (type: string) => {
    switch (type) {
      case "error":
        return "destructive";
      default:
        return "default";
    }
  };

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    notifications.forEach(notification => {
      if (notification.duration) {
        const timer = setTimeout(() => {
          dispatch(removeNotification(notification.id));
        }, notification.duration);
        timers.push(timer);
      }
    });

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [notifications, dispatch]);

  if (notifications.length === 0) return null;

  return (
    <div className='fixed right-4 top-4 z-50 w-80 space-y-2'>
      {notifications.map(notification => (
        <Alert key={notification.id} variant={getVariant(notification.type)} className='relative'>
          {getIcon(notification.type)}
          <AlertTitle>{notification.title}</AlertTitle>
          <AlertDescription>{notification.message}</AlertDescription>
          <Button
            variant='ghost'
            size='sm'
            className='absolute right-2 top-2 h-6 w-6 p-0'
            onClick={() => dispatch(removeNotification(notification.id))}
          >
            <X className='h-3 w-3' />
          </Button>
        </Alert>
      ))}
    </div>
  );
};

export default NotificationContainer;
